import type { CompanionBaseSheetModel } from "@/app/companion/CompanionBaseSheet";

export type CompanionChromeMode = "app" | "example";
export type CompanionChromeSportChip = Readonly<{ key: string; label: string }>;
/** What a page publishes to the frame. "all" is the All sports chip key. */
export type CompanionChromePage = Readonly<{
  mode: CompanionChromeMode;
  placeLabel: string; // "Morzine", "Fistral"
  sportLabel: string; // "Ski", "All sports"
  dayLabel: string; // "Today" or "Wed 6 Jan"
  freshnessLabel?: string | null; // "Updated 07:12"
  sportChips?: readonly CompanionChromeSportChip[];
  defaultSport?: string; // "all" when omitted
  baseSheet?: CompanionBaseSheetModel | null; // signed in only
  signInHref?: string; // example mode
  signupHref?: string; // example mode
}>;

export const COMPANION_ALL_SPORTS = "all";
export const COMPANION_SPORT_STORAGE_KEY = "pw-companion-sport";

const SEPARATOR = " · ";

/** "Morzine · Ski · Today", "Example · Fistral", fallback "Companion". */
export function companionPillText(page: CompanionChromePage | null): string {
  if (!page) return "Companion";
  const place = page.placeLabel.trim();
  if (page.mode === "example") {
    return place ? ["Example", place].join(SEPARATOR) : "Example";
  }
  const parts = [place, page.sportLabel.trim(), page.dayLabel.trim()].filter(Boolean);
  return parts.length ? parts.join(SEPARATOR) : "Companion";
}

/**
 * The selected sport: a stored device choice wins when the page offers it,
 * otherwise the page default, otherwise All.
 */
export function resolveCompanionSport(
  page: CompanionChromePage | null,
  stored: string | null,
): string {
  const fallback = page?.defaultSport ?? COMPANION_ALL_SPORTS;
  if (!stored) return fallback;
  const chips = page?.sportChips;
  if (!chips || chips.length === 0) return stored;
  return chips.some((chip) => chip.key === stored) ? stored : fallback;
}

/** Matches the Supabase auth cookie written by @supabase/ssr. */
export const COMPANION_AUTH_COOKIE_PATTERN = /(^|;\s*)sb-[^=]*-auth-token(\.\d+)?=/;

export function isCompanionPathname(pathname: string): boolean {
  return pathname === "/companion" || pathname.startsWith("/companion/");
}

/** Signed-in Companion routes run as an app: no global site navbar or footer. */
export function isCompanionAppView(pathname: string, cookie: string): boolean {
  return isCompanionPathname(pathname) && COMPANION_AUTH_COOKIE_PATTERN.test(cookie);
}

/**
 * Inline head script for the static root layout. It marks the document before
 * first paint so the global chrome never flashes in the native app. Keep it
 * in sync with `isCompanionAppView`.
 */
export const COMPANION_APP_VIEW_SCRIPT =
  '(function(){try{var p=location.pathname;if((p==="/companion"||p.indexOf("/companion/")===0)&&/(^|;\\s*)sb-[^=]*-auth-token(\\.\\d+)?=/.test(document.cookie)){document.documentElement.setAttribute("data-companion-app","1")}}catch(e){}})();';

/**
 * The public example (`data-companion-showcase`) keeps the site chrome even when an expired
 * session's cookie marked the document: the page is logged out, so it is not the app view.
 */
export const COMPANION_APP_VIEW_STYLE =
  "html[data-companion-app]:not(:has([data-companion-showcase])) [data-site-chrome]{display:none!important}html[data-companion-app]:not(:has([data-companion-showcase])) main#main{padding-top:0!important}";
